import React from 'react';
import {
    Box,
    Flex,
    Spinner,
    Text,
    useToast,
} from '@chakra-ui/react';
import { CustomButton } from '../components/Buttons';
import { useNavigate } from 'react-router-dom';
import useProgressStatus from '../layout/component/ProgressStatus';

const FormNavigation = ({
    // Formik integration
    formik,

    // Navigation
    nextRoute,
    previousRoute,

    // Progress tracking
    progressKey, // The specific progress key for this page (e.g., "personal_info", "poa", etc.)

    // State
    isLoading,
    setIsLoading,

    // Customization
    saveButtonText = "Save",
    nextButtonText = "Next",
    previousButtonText = "Previous",
    showPrevious = true,
    showSave = true,
    showNext = true,

    // Messages
    successMessage = "Data saved successfully!",
    errorMessage = "Error saving data",

    ...boxProps
}) => {
    const navigate = useNavigate();
    const toast = useToast();

    // Use the custom hook to get progress status for this specific page
    const { isCompleted, isLoading: progressLoading, markAsCompleted } = useProgressStatus(progressKey);

    // Check if form has unsaved changes
    const hasUnsavedChanges = formik.dirty;

    // The core logic: Should Next button save or just navigate?
    const shouldNextButtonSave = !isCompleted;

    // Handle save operation (triggers database transaction)
    const handleSave = async (shouldNavigate = false) => {
        try {
            // Trigger form validation
            const errors = await formik.validateForm();
            if (Object.keys(errors).length > 0) {
                formik.setTouched(
                    Object.keys(errors).reduce((acc, key) => {
                        acc[key] = true;
                        return acc;
                    }, {})
                );
                toast({
                    title: "Validation Error",
                    description: "Please fix the errors in the form before saving.",
                    position: "top",
                    status: "error",
                    isClosable: true,
                });
                return;
            }

            setIsLoading(true);

            // This will trigger Formik's onSubmit which hits the database
            await formik.submitForm();

            // ✅ FIXED: Navigate after successful save if shouldNavigate is true
            if (shouldNavigate && nextRoute) {
                markAsCompleted(); // Mark this page as completed
                navigate(nextRoute); // Navigate to next route
            }

        } catch (error) {
            console.error('Save error:', error);
            toast({
                title: "Error",
                description: error.message || errorMessage,
                position: "top",
                status: "error",
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle next button click - THE CORE LOGIC
    const handleNext = () => {
        if (shouldNextButtonSave) {
            // If page was never saved → SAVE & NEXT
            handleSave(true); // This will trigger database transaction and navigate
        } else {
            // If page was previously saved → JUST NEXT (no database call)
            navigate(nextRoute);
        }
    };

    // Handle previous button click
    const handlePrevious = () => {
        if (previousRoute) {
            navigate(previousRoute);
        }
    };

    // Handle save button click
    const handleSaveOnly = () => {
        handleSave(false); // Save without navigation
    };

    // Show loading while checking progress status
    if (progressLoading) {
        return (
            <Box py={5} display="flex" justifyContent="center">
                <Spinner
                    thickness="3px"
                    speed="0.65s"
                    emptyColor="gray.200"
                    color="blue.500"
                    size="md"
                />
            </Box>
        );
    }

    return (
        <Box py={5} gap={5} display={"flex"} flexDir={"row"} {...boxProps} >
            {/* Previous Button */}
            {showPrevious && previousRoute && (
                <CustomButton
                    onClick={handlePrevious}
                    bg={"gray.400"}
                    color={"white"}
                    label={previousButtonText}
                    _hover={{
                        bg: "gray.500",
                    }}
                    isDisabled={isLoading}
                />
            )}

            {/* Save Button */}
            {showSave && (
                <Flex flex={1} justify="center">
                    {isLoading ? (
                        <Spinner
                            thickness="3px"
                            speed="0.65s"
                            emptyColor="gray.200"
                            color="blue.500"
                            size="md"
                        />
                    ) : (
                        <CustomButton
                            onClick={handleSaveOnly}
                            bg={"blue.400"}
                            color={"white"}
                            label={saveButtonText}
                            _hover={{
                                bg: "blue.500",
                            }}
                            isDisabled={isLoading || !hasUnsavedChanges}
                        />
                    )}
                </Flex>
            )}

            {/* Next Button - THE SMART BUTTON */}
            {showNext && nextRoute && (
                <CustomButton
                    onClick={handleNext}
                    bg={shouldNextButtonSave ? "green.400" : "blue.400"}
                    color={"white"}
                    label={shouldNextButtonSave ? 'Save and Next' : nextButtonText}
                    _hover={{
                        bg: shouldNextButtonSave ? "green.500" : "blue.500",
                    }}
                    isDisabled={isLoading}
                />
            )}
        </Box>
    );
};

export default FormNavigation;